import {
  ActionType,
  ConfirmAction,
  CustomAction,
  EmptyListingButtonTypes,
  ExtraFilter,
  FavoriteAccessItem,
  FilterAll,
  FilterComponent,
  getResourceLifecycleStatus,
  InfoBlockProps,
  InfoBlockStatus,
  Listing,
  ListingColumn,
  ListingComponent,
  ListingDisplayNameLink,
  ListingSelectOption,
  Operator,
  optimizedRetryOption,
  SelectionMode,
  SortDirections,
  stateT,
  Status,
  useFavoriteAccess,
  useNavigation,
  ViewSelectOption,
} from "o4a-react";
import * as React from "react";
import { useParams } from "react-router-dom";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { ConsoleContext } from "../../console/ConsoleContext";
import { FilterState, FilterStateContext } from "../../console/FilterContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  ADBS_CREATE_ROUTE,
  APEX_CREATE_ROUTE,
  DetailsPanelId,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  emptyChar,
  ListingTestIds,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
  ViewKeys,
} from "../../constants/uiConstants";
import { AzureSubscriptionSummary } from "../../gen/clients/mchub-azure-api-client";
import {
  AdbsDatabaseBase,
  AdbsDatabaseCollection,
  AdbsDatabaseSummary,
  AdbsDatabaseSummaryStatusEnum,
} from "../../gen/clients/mchub-azure-api-client-adb";
import { parseId } from "../../helpers/idHelper";
import {
  AdbWorkloadType,
  getFieldCustomOptions,
  getStatusInfo,
  getWorkloadTypeDisplayName,
  ResourceStatus,
  responseItemstoArray,
  statusSortComparator,
  workloadTypeSortComparator,
} from "../../helpers/resourceHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFeatures } from "../../hooks/useFeatures";
import { useFilterStatePills } from "../../hooks/useFilterStatePills";
import { useLocationFilters } from "../../hooks/useLocationFilters";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useRowCount } from "../../hooks/useRowCount";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { AdbsDeleteProps, newAdbsDelete } from "../../operations/Adbs/AdbsDelete";
import { AdbsRestartProps, newAdbsRestart } from "../../operations/Adbs/AdbsRestart";
import { AdbsStartProps, newAdbsStart } from "../../operations/Adbs/AdbsStart";
import { AdbsStopProps, newAdbsStop } from "../../operations/Adbs/AdbsStop";
import {
  AdbsViewConnectionStringsProps,
  newAdbsViewConnectionStrings,
} from "../../operations/Adbs/AdbsViewConnectionStrings";
import { getAzureLocationName, getOciRegion } from "../../utils";

const detailsPageRegistrationIds = PageRegistrationConfig[PageId.ADBS_DETAILS].map(config => config.key);

export interface AdbsItem {
  id: string;
  displayName: string;
  subscriptionId: string;
  subscriptionName: string;
  resourceGroupName: string;
  location: string;
  dbName: string;
  dbWorkload: AdbWorkloadType;
  status: AdbsDatabaseSummaryStatusEnum;
  lastOperationStatus?: string;
  lastOperationStatusDetails?: string;
  freeformTags?: { [key: string]: string; };
  isFavorite: boolean;
}

export enum ColumnIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  LifecycleStatus = "status",
  WorkloadType = "dbWorkload",
  FreeformTags = "freeformTags",
}

export enum ColumnTestIds {
  DisplayName = "displayName",
  SubscriptionName = "subscriptionName",
  ResourceGroupName = "resourceGroupName",
  Location = "location",
  LifecycleStatus = "status",
  WorkloadType = "dbWorkload",
  FreeformTags = "freeformTags",
}

export enum ActionBarIds {
  Create = "action-bar-create",
  Start = "action-bar-start",
  Stop = "action-bar-stop",
  Restart = "action-bar-restart",
  ConnectionStrings = "action-bar-connection-strings",
  Refresh = "action-bar-refresh",
  Delete = "action-bar-delete",
}

export enum ActionMenuIds {
  Start = "action-menu-start",
  Stop = "action-menu-stop",
  Restart = "action-menu-restart",
  ConnectionStrings = "action-menu-connection-strings",
  Delete = "action-menu-delete",
  AddFavorite = "action-menu-add-favorite",
  RemoveFavorite = "action-menu-remove-favorite",
}

export enum ActionBarTestIds {
  Create = "action-bar-create",
  Start = "action-bar-start",
  Stop = "action-bar-stop",
  Restart = "action-bar-restart",
  ConnectionStrings = "action-bar-connection-strings",
  Refresh = "action-bar-refresh",
  Delete = "action-bar-delete",
}

export enum ActionMenuTestIds {
  Start = "action-menu-start",
  Stop = "action-menu-stop",
  Restart = "action-menu-restart",
  ConnectionStrings = "action-menu-connection-strings",
  Delete = "action-menu-delete",
  AddFavorite = "action-menu-add-favorite",
  RemoveFavorite = "action-menu-remove-favorite",
}

export enum FilterTestIds {
  SubscriptionFilter = "subscription-filter",
  LocationFilter = "location-filter",
}

export enum InfoBlockTestIds {
  DisabledActions = "disabled-actions-info-block"
}

interface AzureSubscriptionSummaryMap {
  [key: string]: AzureSubscriptionSummary;
}

export interface AdbsListProps {
  isApex?: boolean;
  disabled?: boolean;
}

export const AdbsList = ({ isApex, disabled }: AdbsListProps): JSX.Element => {
  const { enableApex } = useFeatures();

  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { favoriteItems, isFavorite, setAsFavorite, removeFromFavorites } = useFavoriteAccess();

  const { navigateTo } = useNavigation(ConsoleContext);
  const { preferredSubscription, locale } = React.useContext<Settings>(SettingsContext);
  const filterState = React.useContext<FilterState>(FilterStateContext);
  const { listId } = useParams();

  const [selection, setSelection] = React.useState<AdbsItem[]>([]);
  const [selectedSubscription, setSelectedSubscription] = React.useState<string>("");
  const [subscriptionOptions, setSubscriptionOptions] = React.useState<{ key: string, text: string }[]>([]);
  const { loading: subscriptionLoading, subscriptions } = useSubscriptions();

  const { locationOptions, selectedLocation, setSelectedLocation } = useLocationFilters(
    filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
  );

  const [listingComponentRef, setListingComponentRef] = React.useState<ListingComponent<AdbsItem>>();

  const [filterComponentRef, setFilterComponentRef] = React.useState<FilterComponent>();
  const pillFilterValues = filterComponentRef?.getFilters();
  const filterTextValue = filterComponentRef?.getFilterText();
  const [filterText, setFilterText] = React.useState(filterState.filterTextValue || filterTextValue);

  const { perPageItemsCount } = useRowCount();

  const getLocation = (value: AdbsItem): string => getAzureLocationName(value.location);

  const getStatus = (value: AdbsItem): JSX.Element => (
    <Status
      label={stateT(value?.status || "")}
      tooltip={stateT(value?.status || "")}
      status={getResourceLifecycleStatus(value?.status || "")}
      statusInfo={
        getStatusInfo(
          value?.status,
          value?.lastOperationStatus || "",
          value?.lastOperationStatusDetails || "",
        )
      }
      hideClipboardCopy
    />
  );

  const getWorkloadType = (value: AdbsItem): string => getWorkloadTypeDisplayName(value.dbWorkload);

  const columns: ListingColumn[] = [
    {
      itemProp: ColumnIds.DisplayName,
      testId: ColumnTestIds.DisplayName,
      name: Messages.common.name(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => (
        <ListingDisplayNameLink
          displayName={value.displayName}
          navigation={{
            // eslint-disable-next-line max-len
            to: `${RESOURCE_ROUTE}/${value.id}/${PageRegistrationConfig[PageId.ADBS_DETAILS][0].panelPath}?location=${value.location}${(isApex ? "&type=apex" : "")}`,
            pageKey: detailsPageRegistrationIds[0],
          }}
          iconName={isApex ? SvgIconIds.apexSvg : SvgIconIds.adbSvg}
        />
      ),
    },
    {
      itemProp: ColumnIds.SubscriptionName,
      testId: ColumnTestIds.SubscriptionName,
      name: Messages.labels.subscription(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components, max-len
      onRenderItems: value => <AzureSubscriptionLink resourceId={value.id} subscriptionName={value.subscriptionName} hideClipboard />,
    },
    {
      itemProp: ColumnIds.ResourceGroupName,
      testId: ColumnTestIds.ResourceGroupName,
      name: Messages.labels.resourceGroup(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      // eslint-disable-next-line react/no-unstable-nested-components
      onRenderItems: value => <AzureResourceGroupLink resourceId={value.id} hideClipboard />,
    },
    {
      itemProp: ColumnIds.Location,
      testId: ColumnTestIds.Location,
      name: Messages.labels.location(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      isResizable: true,
      onRenderItems: value => getLocation(value),
    },
    {
      itemProp: ColumnIds.LifecycleStatus,
      testId: ColumnTestIds.LifecycleStatus,
      name: Messages.labels.status(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: AdbsItem,
        b: AdbsItem,
      ) => statusSortComparator<AdbsItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getStatus(value),
    },
    {
      itemProp: ColumnIds.WorkloadType,
      testId: ColumnTestIds.WorkloadType,
      name: Messages.labels.workloadType(),
      flexGrow: 2,
      initialSortDirection: SortDirections.ASC,
      comparator: (
        a: AdbsItem,
        b: AdbsItem,
      ) => workloadTypeSortComparator<AdbsItem>(a, b, locale),
      isResizable: true,
      onRenderItems: value => getWorkloadType(value),
    },
  ];

  const { refresh: adbsRefresh, loading: adbsLoading, response: adbsResponse } = useQueryCall({
    wait: (!selectedSubscription || !selectedLocation) || disabled,
    method: apiClients.withRegion(
      getOciRegion(selectedLocation),
    ).adbsDatabaseApi.listAdbsDatabasesBySubscription,
    options: {
      args: {
        subscriptionId: selectedSubscription,
        apiVersion: MultiCloudDatabaseApiVersion,
      },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadAdbs(),
      },
    },
  });

  const adbs = adbsResponse
    && responseItemstoArray<AdbsDatabaseCollection, AdbsDatabaseSummary>(adbsResponse);

  const groupingOptions: ListingSelectOption<AdbsItem>[] = [];

  const viewOptions: ViewSelectOption<AdbsItem>[] = [
    {
      key: ViewKeys.ListView,
      text: Messages.listAdbs.filters.listView(),
      icon: MonochromeIconIds.List,
    },
  ];

  const adbsSummaryToAdbsItem = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    items: any[],
    subscriptionsMap: AzureSubscriptionSummaryMap,
  ): AdbsItem[] => {
    const adbsItems: AdbsItem[] = [];
    items.forEach((item: AdbsDatabaseBase) => {
      const { subscriptionId, resourceGroup } = parseId(item.id);
      const itemSubscriptionName = subscriptionsMap && subscriptionsMap[subscriptionId]?.name;

      if (!enableApex || (isApex && item.dbWorkload === AdbWorkloadType.APEX)
        || (!isApex && item.dbWorkload !== AdbWorkloadType.APEX)) {
        adbsItems.push({
          id: item.id,
          displayName: item.name,
          subscriptionId,
          subscriptionName: itemSubscriptionName,
          resourceGroupName: resourceGroup,
          location: item.location,
          dbName: item.dbName,
          dbWorkload: item.dbWorkload as AdbWorkloadType,
          status: item.status,
          freeformTags: item.freeformTags,
          isFavorite: isFavorite(item.id),
        });
      }
    });
    return adbsItems;
  };

  const items = React.useMemo(() => {
    let newInstance: AdbsItem[] = [];

    if (adbs && subscriptions && subscriptions.length > 0) {
      const subscriptionsMap = subscriptions.reduce((
        map: AzureSubscriptionSummaryMap,
        subscription: AzureSubscriptionSummary,
      ) => {
        map[subscription.id] = subscription;
        return map;
      }, {});
      newInstance = adbsSummaryToAdbsItem(adbs, subscriptionsMap);
    }
    return newInstance;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(adbs), JSON.stringify(subscriptions), JSON.stringify(favoriteItems)]);

  React.useEffect(() => {
    const newSubscriptionOptions: { key: string, text: string }[] = [];
    // eslint-disable-next-line no-unused-expressions
    subscriptions && subscriptions.forEach(subscription => {
      newSubscriptionOptions.push({ key: subscription.id, text: subscription.name });
    });

    setSubscriptionOptions(newSubscriptionOptions);
    if (filterState.defaultSubscription) {
      setSelectedSubscription(filterState.defaultSubscription);
      if (defaultExtraFilters) defaultExtraFilters[0].value = filterState.defaultSubscription;
    } else if (preferredSubscription) {
      setSelectedSubscription(preferredSubscription);
    } else {
      setSelectedSubscription(newSubscriptionOptions.length ? newSubscriptionOptions[0].key : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(subscriptions)]);

  const subscriptionFilter: ExtraFilter = {
    testId: FilterTestIds.SubscriptionFilter,
    name: columns[1].name,
    operator: Operator.Equal,
    value: filterState.defaultSubscription ? filterState.defaultSubscription : selectedSubscription,
    fieldKey: columns[1].itemProp,
    customOptions: subscriptionOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedSubscription(value);
        filterState.setDefaultSubscription(value);
      }
    },
    hideDelete: true,
  };

  const resourceGroupFilter: ExtraFilter = {
    name: columns[2].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[2].itemProp)?.value || [FilterAll],
    fieldKey: columns[2].itemProp,
    customOptions: getFieldCustomOptions(items?.map(item => item.resourceGroupName), (fieldVal: string) => fieldVal),
  };

  React.useEffect(() => {
    if (filterState.defaultLocation) {
      setSelectedLocation(filterState.defaultLocation);
      if (defaultExtraFilters) defaultExtraFilters[1].value = filterState.defaultLocation;
    } else {
      setSelectedLocation(filterState.pillFilterStateValue.filterValues?.[1]?.value || selectedLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locationFilter: ExtraFilter = {
    testId: FilterTestIds.LocationFilter,
    name: columns[3].name,
    operator: Operator.Equal,
    value: filterState.defaultLocation ? filterState.defaultLocation : selectedLocation,
    fieldKey: columns[3].itemProp,
    customOptions: locationOptions,
    hideAllOption: true,
    callBack: (value: string | string[]) => {
      if (!Array.isArray(value)) {
        setSelectedLocation(value);
        filterState.setDefaultLocation(value);
      }
    },
    hideDelete: true,
  };

  const lifecycleStateFilter: ExtraFilter = {
    name: columns[4].name,
    operator: Operator.Equal,
    value: pillFilterValues?.find(item => item.fieldKey === columns[4].itemProp)?.value || [FilterAll],
    fieldKey: columns[4].itemProp,
    messageCallBack: stateT,
    customOptions: getFieldCustomOptions(
      items?.map(item => item.status || ""),
      (fieldVal: string) => {
        if (fieldVal) {
          return stateT(fieldVal);
        }
        return emptyChar;
      },
    ),
  };

  const allFilterPills = [
    subscriptionFilter,
    locationFilter,
    resourceGroupFilter,
    lifecycleStateFilter,
  ];

  const filterPills = [
    subscriptionFilter,
    locationFilter,
  ];

  const { trigger: triggerDeleteAdbs } = useOperation<AdbsDeleteProps>(newAdbsDelete);
  const { trigger: triggerStartAdbs } = useOperation<AdbsStartProps>(newAdbsStart);
  const { trigger: triggerStopAdbs } = useOperation<AdbsStopProps>(newAdbsStop);
  const { trigger: triggerRestartAdbs } = useOperation<AdbsRestartProps>(newAdbsRestart);
  const { trigger: triggerViewConnectionStrings } = useOperation<AdbsViewConnectionStringsProps>(
    newAdbsViewConnectionStrings,
  );
  const { closePanels } = useSidePanel();

  const confirmDeleteAction = (selectedItem: AdbsItem) : void => {
    triggerDeleteAdbs({
      databaseId: selectedItem.id,
      location: selectedLocation,
      onExecute: () => {
        adbsRefresh();
        listingComponentRef?.resetTableSelection();
      },
    });
  };

  const confirmStartAction = (selectedItem: AdbsItem) : void => {
    triggerStartAdbs({
      databaseId: selectedItem.id,
      location: selectedLocation,
      onExecute: () => {
        adbsRefresh();
        listingComponentRef?.resetTableSelection();
      },
    });
  };

  const confirmStopAction = (selectedItem: AdbsItem): void => {
    triggerStopAdbs({
      databaseId: selectedItem.id,
      location: selectedLocation,
      onExecute: () => {
        adbsRefresh();
        listingComponentRef?.resetTableSelection();
      },
    });
  };

  const confirmRestartAction = (selectedItem: AdbsItem): void => {
    triggerRestartAdbs({
      databaseId: selectedItem.id,
      location: selectedLocation,
      onExecute: () => {
        adbsRefresh();
        listingComponentRef?.resetTableSelection();
      },
    });
  };

  const confirmViewConnectionStringsAction = (selectedItem: AdbsItem) : void => {
    triggerViewConnectionStrings({
      databaseId: selectedItem.id,
      location: selectedLocation,
    });
  };

  const disabledActionsInfoBlocks = (selectedItem: AdbsItem): InfoBlockProps[] => {
    const disabledActionInfo: InfoBlockProps[] = [];
    const disabledActions: string[] = [];
    if (selectedItem?.status === ResourceStatus.Terminating
      || selectedItem?.status === ResourceStatus.Updating) {
      disabledActions.push(Messages.actions.start());
      disabledActions.push(Messages.actions.stop());
      disabledActions.push(Messages.actions.restart());
     
      // eslint-disable-next-line max-len
      const disabledMessage = Messages.hints.adbsDisabledActionsAdbsStatus(disabledActions.join(", "), stateT(selectedItem?.status || ""));
      disabledActionInfo.push(
        {
          testId: InfoBlockTestIds.DisabledActions,
          message: disabledMessage,
          messageType: InfoBlockStatus.INFO,
        },
      );
    } else if (selectedItem?.status === ResourceStatus.Failed) {
      disabledActions.push(Messages.actions.start());
      disabledActions.push(Messages.actions.stop());
      // eslint-disable-next-line max-len
      const disabledMessage = Messages.hints.adbsDisabledActionsAdbsStatus(disabledActions.join(", "), stateT(selectedItem?.status || ""));
      disabledActionInfo.push(
        {
          testId: InfoBlockTestIds.DisabledActions,
          message: disabledMessage,
          messageType: InfoBlockStatus.INFO,
        },
      );
    }
    return disabledActionInfo;
  };

  const buildStartAction = (key: string, testId: string, selectedItem: AdbsItem)
  : ConfirmAction<AdbsItem | undefined> => {
    const startAction = {
      key,
      testId,
      text: Messages.actions.start(),
      icon: MonochromeIconIds.Play,
      title: Messages.labels.startAdbs(),
      description: Messages.hints.startAdbsConfirmation(selectedItem?.displayName),
      disabled: selectedItem?.status !== ResourceStatus.Stopped,
      onClick: () => trackActionClick(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
      onConfirm: () => confirmStartAction(selectedItem),
      onCancel: () => trackActionDiscard(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),

    };
    return startAction;
  };
  const buildStopAction = (key: string, testId: string, selectedItem: AdbsItem)
  : ConfirmAction<AdbsItem | undefined> => {
    const stopAction = {
      key,
      testId,
      text: Messages.actions.stop(),
      icon: MonochromeIconIds.Stop,
      title: Messages.labels.stopAdbs(),
      description: Messages.hints.stopAdbsConfirmation(selectedItem?.displayName),
      disabled: selectedItem?.status !== ResourceStatus.Active,
      onClick: () => trackActionClick(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
      onConfirm: () => confirmStopAction(selectedItem),
      onCancel: () => trackActionDiscard(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),

    };
    return stopAction;
  };

  const buildRestartAction = (key: string, testId: string, selectedItem: AdbsItem)
  : ConfirmAction<AdbsItem | undefined> => {
    const restartAction = {
      key,
      testId,
      text: Messages.actions.restart(),
      icon: MonochromeIconIds.Restart,
      title: Messages.labels.restartAdbs(),
      description: Messages.hints.restartAdbsConfirmation(selectedItem?.displayName),
      disabled: selectedItem?.status !== ResourceStatus.Active
        && selectedItem?.status !== ResourceStatus.Failed,
      onClick: () => trackActionClick(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
      onConfirm: () => confirmRestartAction(selectedItem),
      onCancel: () => trackActionDiscard(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
    };
    return restartAction;
  };

  const buildConnectionStringsAction = (key: string, testId: string, selectedItem: AdbsItem) :
  CustomAction<AdbsItem | undefined> => {
    const connectionStringsAction = {
      key,
      testId,
      text: Messages.actions.connectionStrings(),
      icon: MonochromeIconIds.Connect,
      disabled: !selectedItem,
      onClick: () => {
        trackActionClick(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
        confirmViewConnectionStringsAction(selectedItem);
      },
    };
    return connectionStringsAction;
  };

  const buildDeleteAction = (key: string, testId: string, selectedItem: AdbsItem)
   : ConfirmAction<AdbsItem | undefined> => {
    const deleteAction = {
      key,
      testId,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      title: Messages.labels.deleteAdbs(),
      disabled: !selectedItem,
      description: Messages.hints.deleteAdbsConfirmation(selectedItem?.displayName),
      onClick: () => trackActionClick(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
      onConfirm: () => confirmDeleteAction(selectedItem),
      onCancel: () => trackActionDiscard(key, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES),
    };
    return deleteAction;
  };

  const adbsRowActionMenus = (item?: AdbsItem | undefined) : ActionType<AdbsItem>[] => {
    const adbsActionItems: ActionType<AdbsItem | undefined>[] = [];
    if (item) {
      if (item.isFavorite) {
        adbsActionItems.push({
          key: ActionMenuIds.RemoveFavorite,
          testId: ActionMenuTestIds.RemoveFavorite,
          text: Messages.actions.removeFromFavorites(),
          icon: MonochromeIconIds.FavoriteSet,
          onClick: () => {
            trackActionClick(ActionMenuIds.RemoveFavorite, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
            removeFromFavorites(item.id);
          },
        } as CustomAction<AdbsItem | undefined>);
      } else {
        adbsActionItems.push({
          key: ActionMenuIds.AddFavorite,
          testId: ActionMenuTestIds.AddFavorite,
          text: Messages.actions.addToFavorites(),
          icon: MonochromeIconIds.FavoriteNotSet,
          onClick: () => {
            trackActionClick(ActionMenuIds.AddFavorite, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
            setAsFavorite({ id: item.id, data: { location: item.location } } as FavoriteAccessItem);
          },
        } as CustomAction<AdbsItem | undefined>);
      }

      adbsActionItems.push(
        buildStartAction(ActionMenuIds.Start, ActionMenuTestIds.Start, item),
        buildStopAction(ActionMenuIds.Stop, ActionMenuTestIds.Stop, item),
        buildRestartAction(ActionMenuIds.Restart, ActionMenuTestIds.Restart, item),
        buildConnectionStringsAction(ActionMenuIds.ConnectionStrings, ActionMenuTestIds.ConnectionStrings, item),
        buildDeleteAction(ActionMenuIds.Delete, ActionMenuTestIds.Delete, item),
      );
    }
    return adbsActionItems;
  };

  const adbsBarActions : ActionType<AdbsItem>[] = [
    {
      key: ActionBarIds.Create,
      testId: ActionBarTestIds.Create,
      text: Messages.actions.create(),
      icon: MonochromeIconIds.Create,
      onClick: () => {
        trackActionClick(ActionBarIds.Create, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
        const route = isApex ? APEX_CREATE_ROUTE : ADBS_CREATE_ROUTE;
        navigateTo(route, PageId.ADBS_CREATE, {
          analytics: {
            pageId: PageId.ADB_LIST_HUB,
            panelId: DetailsPanelId.ADBS_DATABASES,
            actionName: ActionBarIds.Create,
          } as NavigationAnalyticsData,
        });
      },
    },
    buildStartAction(ActionBarIds.Start, ActionBarTestIds.Start, selection[0]),
    buildStopAction(ActionBarIds.Stop, ActionBarIds.Stop, selection[0]),
    buildRestartAction(ActionBarIds.Restart, ActionBarIds.Restart, selection[0]),
    buildConnectionStringsAction(ActionBarIds.ConnectionStrings, ActionBarTestIds.ConnectionStrings, selection[0]),
    {
      key: ActionBarIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      disabled,
      onClick: () => {
        trackActionClick(ActionBarIds.Refresh, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
        adbsRefresh();
        listingComponentRef?.resetTableSelection();
      },
    },
    buildDeleteAction(ActionBarIds.Delete, ActionBarTestIds.Delete, selection[0]),
  ];

  const setSelectedItems = (selectedItems: AdbsItem[]): void => {
    setSelection(selectedItems);
  };

  const textFilterCallBack = (rowItem: AdbsItem): AdbsItem => {
    const displayRowItem = { ...rowItem };
    displayRowItem.status = stateT(
      displayRowItem.status,
    ) as AdbsDatabaseSummaryStatusEnum;
    displayRowItem.location = getAzureLocationName(displayRowItem.location);
    return displayRowItem;
  };

  React.useEffect(() => {
    setFilterText(filterTextValue);
    filterState.setFilterTextValue(filterTextValue);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTextValue]);

  const defaultExtraFilters = useFilterStatePills(pillFilterValues, allFilterPills, listId, filterPills);

  const resetFilterValues = (): void => {
    const defaultPillValues = filterPills.map(filter => ({ fieldKey: filter.fieldKey, value: filter.value as string }));
    filterState.setFilterStateValue({ filterValues: defaultPillValues, listId, defaultPills: filterPills });
    filterState.setFilterTextValue("");
    setFilterText("");
  };

  return (
    <Listing
      testId={ListingTestIds.Adbs}
      items={items}
      listColumns={columns}
      infoBlocks={disabledActionsInfoBlocks(selection[0])}
      listingComponentRef={setListingComponentRef}
      itemsPerPage={perPageItemsCount}
      actionBarItems={adbsBarActions as ActionType<undefined>[]}
      onActionClick={closePanels}
      selectionMode={SelectionMode.single}
      selectedItems={setSelectedItems}
      actions={adbsRowActionMenus}
      emptyList={{
        title: isApex ? Messages.listApex.emptyList.title() : Messages.listAdbs.emptyList.title(),
        emptyButtons: [
          {
            type: EmptyListingButtonTypes.PRIMARY,
            text: isApex ? Messages.actions.createApex() : Messages.actions.createAdbs(),
            action: () => {
              trackActionClick(ActionBarIds.Create, PageId.ADB_LIST_HUB, DetailsPanelId.ADBS_DATABASES);
              const route = isApex ? APEX_CREATE_ROUTE : ADBS_CREATE_ROUTE;
              navigateTo(route, PageId.ADBS_CREATE);
            },
          },
        ],
        watermark: isApex ? SvgIconIds.apexWatermarkSvg : SvgIconIds.adbWatermarkSvg,
      }}
      isLoading={(adbsLoading || subscriptionLoading) && !disabled}
      sorting={{
        locale,
        initialSortedColumn: ColumnIds.DisplayName,
      }}
      groupingSelect={{ groupingOptions }}
      viewSelect={{
        viewOptions,
        defaultSelectedKey: ViewKeys.ListView,
      }}
      filtering={{
        allExtraFilters: allFilterPills,
        defaultExtraFilters,
        defaultFilterText: filterText,
        textFilterCallBack,
        componentRef: setFilterComponentRef,
        onClearFilters: resetFilterValues,
      }}
    />
  );
};
